import { ADAPTER_ROWS_PROP } from './constants';
import type { AdapterFailure, RenderAdapterProjection } from './types';

/**
 * Running a component's registered adapter, with every way it can fail
 * reduced to one shape.
 *
 * The nine adapters in `RENDER_ADAPTER_REGISTRY` report a missing column by
 * returning `{ props, error }` and report a programming mistake by throwing.
 * A caller that handles only one of those renders a chart with no data and no
 * explanation for the other, so both collapse to `AdapterFailure` here and
 * neither reaches the component as a silent empty prop object.
 */

/** Shape the existing adapters return. A projection may return props directly. */
interface AdapterResultLike {
  readonly props?: unknown;
  readonly error?: unknown;
  readonly malformed?: boolean;
}

export interface AdapterProjectionOutcome {
  /** Props to merge beneath authored props. Empty when the adapter failed. */
  readonly props: Record<string, unknown>;
  readonly failure?: AdapterFailure;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * True for the `{ props, error? }` envelope rather than a bare prop object.
 *
 * Keyed on `props` because that is the field the envelope always carries;
 * testing for `error` would classify a successful envelope as bare props and
 * hand the component `{ props: {...} }` as its prop object.
 */
function isAdapterResult(value: unknown): value is AdapterResultLike {
  return isRecord(value) && 'props' in value;
}

function toFailure(error: unknown): AdapterFailure {
  if (isRecord(error)) {
    const code = typeof error.code === 'string' ? error.code : 'adapter_error';
    const message =
      typeof error.message === 'string' ? error.message : String(error);
    const missingColumns = Array.isArray(error.missingColumns)
      ? error.missingColumns.filter(
          (column): column is string => typeof column === 'string',
        )
      : undefined;
    return missingColumns?.length
      ? { code, message, missingColumns }
      : { code, message };
  }
  return {
    code: 'adapter_error',
    message: error instanceof Error ? error.message : String(error),
  };
}

/**
 * Runs `adapter` over the value bound to `_bind.rows`.
 *
 * `config` is the resolved prop bag minus the rows slot: an authored chart
 * node's props *are* the adapter's configuration, which is what keeps the
 * document the single description of the surface. Passing the props through
 * unread would mean the host assembles a config object and the capability is
 * only half of one.
 */
export function runAdapterProjection(
  adapter: RenderAdapterProjection,
  resolvedProps: Readonly<Record<string, unknown>>,
  onError: (error: unknown, source: string) => void,
): AdapterProjectionOutcome {
  const { [ADAPTER_ROWS_PROP]: rows, ...config } = resolvedProps;

  let returned: unknown;
  try {
    returned = adapter(rows, config as never);
  } catch (error) {
    onError(error, 'binding-enhancer/adapter-projection');
    return { props: {}, failure: toFailure(error) };
  }

  if (isAdapterResult(returned)) {
    // Props are kept even on the error path. A failing adapter returns the
    // authored presentation with no rows on purpose -- so the surface renders
    // its own configured empty state rather than a blank frame -- and
    // discarding them would produce exactly the frame it took care to avoid.
    const props = isRecord(returned.props) ? returned.props : {};
    return returned.error !== undefined
      ? { props, failure: toFailure(returned.error) }
      : { props };
  }

  if (isRecord(returned)) {
    return { props: returned };
  }

  // An adapter that returns a scalar has no props to merge, and merging
  // nothing while reporting success would render a chart with authored props
  // only -- indistinguishable from a chart nobody bound.
  const failure: AdapterFailure = {
    code: 'adapter_returned_non_object',
    message: `Adapter returned ${returned === null ? 'null' : typeof returned} rather than a prop object`,
  };
  onError(new Error(failure.message), 'binding-enhancer/adapter-projection');
  return { props: {}, failure };
}

/**
 * Layers prop objects lowest-first, letting a defined value win over an
 * `undefined` one regardless of layer.
 *
 * A plain spread cannot be used here. Adapters emit optional fields as
 * explicit `undefined` keys (`chartPresentationProps` always writes `height`),
 * and a binding that fails to resolve produces an `undefined` authored value.
 * Under spread semantics either one erases a real value from the layer below,
 * so a catalog default disappears because an adapter did not compute that
 * field, and a computed field disappears because an author's binding pointed
 * at a path that was not there. Both read as "the prop was never set", which
 * is the hardest kind of missing prop to trace.
 *
 * React draws no distinction between an absent prop and an `undefined` one, so
 * skipping them costs nothing a caller can observe.
 */
export function mergeDefinedProps(
  ...layers: ReadonlyArray<Readonly<Record<string, unknown>> | undefined>
): Record<string, unknown> {
  const merged: Record<string, unknown> = {};
  for (const layer of layers) {
    if (!layer) continue;
    for (const [key, value] of Object.entries(layer)) {
      if (value !== undefined || !(key in merged)) {
        merged[key] = value;
      }
    }
  }
  return merged;
}

/**
 * Prop paths holding a value that would not survive `JSON.parse(JSON.stringify(...))`.
 *
 * The reason this checks the MERGED object rather than the adapter's output is
 * the merge itself: adapter output is already held to a serializable contract
 * by `adapterOutputContract.test.ts`, so a guard that stopped there would be
 * defeated by an author writing a function into the same prop object. A chart
 * whose props are not data all the way down cannot be sent anywhere -- not to
 * a server renderer, not into a cache, not through a document round trip --
 * and the failure shows up far from the prop that caused it.
 *
 * Event handlers are excluded by the caller, not here: they are legitimately
 * functions and always will be.
 */
export function findNonSerializableProps(
  props: Readonly<Record<string, unknown>>,
): string[] {
  const offenders: string[] = [];

  // `ancestors` holds the current path only, added on the way down and removed
  // on the way back up. Copying it at each node instead — which this used to
  // do — allocates a set per node and makes the walk quadratic in depth on the
  // row arrays it spends nearly all its time in. The two agree: a node reached
  // twice through sibling branches is not a cycle either way, because the
  // first branch removes it before the second runs.
  const walk = (value: unknown, path: string, ancestors: Set<object>): void => {
    if (typeof value === 'function' || typeof value === 'symbol') {
      offenders.push(path);
      return;
    }
    if (typeof value === 'bigint') {
      offenders.push(path);
      return;
    }
    if (typeof value !== 'object' || value === null) return;
    if (ancestors.has(value)) {
      // A cycle is non-serializable for the same reason a function is, and
      // recursing into one would hang rather than report it.
      offenders.push(path);
      return;
    }
    if (
      value instanceof Date ||
      value instanceof Map ||
      value instanceof Set ||
      value instanceof RegExp
    ) {
      offenders.push(path);
      return;
    }
    ancestors.add(value);
    if (Array.isArray(value)) {
      value.forEach((entry, index) =>
        walk(entry, `${path}[${index}]`, ancestors),
      );
    } else {
      for (const [key, entry] of Object.entries(value)) {
        walk(entry, path ? `${path}.${key}` : key, ancestors);
      }
    }
    ancestors.delete(value);
  };

  for (const [key, value] of Object.entries(props)) {
    walk(value, key, new Set());
  }

  return offenders;
}
